import { ReactElement } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@mui/material';
import { flexRender, Table } from '@tanstack/react-table';
import clsx from 'clsx';

import type { ScanRes } from '../types/Scan';
import Loader from './Loader';
import Paginator from './Paginator';

import '../styles/components/GenericTable.less';

type Props = {
    table: Table<ScanRes & { listItemUrl: string }>;
    loading?: boolean;
    totalItemsCount?: number;
    setPageSize?: (size: number) => void;
    setCurrentPage?: (index: number) => void;
    currentPage?: number;
    pageSize?: number;
};

const ScanningOverviewTable = ({
    table,
    loading,
    totalItemsCount,
    setPageSize,
    setCurrentPage,
    currentPage,
    pageSize
}: Props): ReactElement => {
    const { rows } = table.getRowModel();

    return (
        <div data-testid="generic-table" className="table-container scan-overview-table">
            {table.getHeaderGroups().map((headerGroup) => (
                <div key={headerGroup.id} className="header-row">
                    {headerGroup.headers.map((header) => {
                        return (
                            <div key={header.id} className={`cell col-${header.index}`}>
                                {flexRender(header.column.columnDef.header, header.getContext())}
                            </div>
                        );
                    })}
                </div>
            ))}
            <div className="rows-container">
                {rows.length > 0 && loading && <Loader size={20} loading={loading} />}

                {rows.length === 0 && (
                    <div data-testid="empty-table" className="empty-table">
                        {loading ? (
                            <Loader size={20} loading={loading} />
                        ) : (
                            <>
                                <FontAwesomeIcon icon={['fad', 'box-open']} size="8x" id="blue" />
                                <div className="no-results-text">No results found</div>
                            </>
                        )}
                    </div>
                )}

                {rows.length > 0 &&
                    rows.map((row) => {
                        const rowUrl = row.original.listItemUrl;

                        return (
                            <div key={row.id} className={clsx('row', { 'link-row': !!rowUrl })}>
                                {row.getVisibleCells().map((cell, cellIndex) =>
                                    rowUrl && cell.column.id !== 'actions' ? (
                                        <Link
                                            key={cell.id}
                                            component={RouterLink}
                                            className={`cell col-${cellIndex}`}
                                            to={rowUrl}
                                            underline="none"
                                            color="inherit"
                                        >
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </Link>
                                    ) : (
                                        <div key={cell.id} className={`cell col-${cellIndex}`}>
                                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                        </div>
                                    )
                                )}
                            </div>
                        );
                    })}
            </div>

            <div className="footer-row">
                {currentPage && pageSize && !!totalItemsCount && setCurrentPage && setPageSize && (
                    <Paginator
                        currentPage={currentPage}
                        pageSize={pageSize}
                        totalItemsCount={totalItemsCount}
                        setCurrentPage={setCurrentPage}
                        setPageSize={setPageSize}
                    />
                )}
            </div>
        </div>
    );
};

export default ScanningOverviewTable;
